import type { BlockSerialized } from '#layers/block-engine/types/block';
import { isNil } from '#layers/base/public';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';

export interface ProcessMoveBlockOptions {
	editedBlock: BlockSerialized;
	childId: string;
	from: {
		parentChildId?: string | null;
		slot?: string | null;
	};
	to: {
		parentChildId?: string | null;
		slot?: string | null;
		index: number;
	};
}

export function processMoveBlock(options: ProcessMoveBlockOptions): {
	data: Partial<BlockSerialized>;
} {
	const {
		editedBlock,
		childId,
		from,
		to,
	} = options;

	const data = {
		children: { ...editedBlock.children },
		rootChildren: editedBlock.rootChildren?.slice() ?? [],
	};

	// Move in root children
	if (isNil(from.parentChildId) && isNil(to.parentChildId)) {
		// Keep positions stable by 'soft' removing the id
		const rootChildrenWithSoftDelete = data.rootChildren.map((id) => id === childId ? null : id);
		// Add it back at the new position
		rootChildrenWithSoftDelete.splice(to.index, 0, childId);
		// Filter out the 'soft' removed ids
		data.rootChildren = rootChildrenWithSoftDelete.filter(Boolean) as string[];
	}
	// Move in same parent and same slot
	// Disable `eqeqeq` rule to handle slot `null == undefined`
	// eslint-disable-next-line eqeqeq
	else if (from.parentChildId != null && from.parentChildId === to.parentChildId && from.slot == to.slot) {
		const parentChild = data.children?.[from.parentChildId];

		if (isBlockSerializedChild(parentChild)) {
			const slot = from.slot ?? 'default';
			const slotChildren = parentChild.slots[slot]?.slice() ?? [];
			// Keep positions stable by 'soft' removing the id
			const slotChildrenWithSoftDelete = slotChildren.map((id) => id === childId ? null : id);
			// Add it back at the new position
			slotChildrenWithSoftDelete.splice(to.index, 0, childId);

			// Filter out the 'soft' removed ids
			data.children = {
				...data.children,
				[from.parentChildId]: {
					...parentChild,
					slots: {
						...parentChild.slots,
						[slot]: slotChildrenWithSoftDelete.filter(Boolean) as string[],
					},
				},
			};
		}
		else if (isBlockSerializedSlotOutlet(parentChild)) {
			const fallback = parentChild.fallback?.slice() ?? [];
			// Keep positions stable by 'soft' removing the id
			const fallbackWithSoftDelete = fallback.map((id) => id === childId ? null : id);
			// Add it back at the new position
			fallbackWithSoftDelete.splice(to.index, 0, childId);

			// Filter out the 'soft' removed ids
			data.children = {
				...data.children,
				[from.parentChildId]: {
					...parentChild,
					fallback: fallbackWithSoftDelete.filter(Boolean) as string[],
				},
			};
		}
		else {
			throw new Error(`Invalid parent child with id ${from.parentChildId}`);
		}
	}
	else {
		// Remove from the old parent

		if (from.parentChildId) {
			const slot = from.slot ?? 'default';
			const parentData = data.children?.[from.parentChildId];

			if (isBlockSerializedChild(parentData)) {
				data.children = {
					...data.children,
					[from.parentChildId]: {
						...parentData,
						slots: {
							...parentData.slots,
							[slot]: parentData.slots[slot]?.filter((id) => id !== childId) ?? [],
						},
					},
				};
			}
			else if (isBlockSerializedSlotOutlet(parentData)) {
				data.children = {
					...data.children,
					[from.parentChildId]: {
						...parentData,
						fallback: parentData.fallback?.filter((id) => id !== childId),
					},
				};
			}
			else {
				throw new Error(`Invalid from parent child with id ${from.parentChildId}`);
			}
		}
		else {
			data.rootChildren = data.rootChildren.filter((id) => id !== childId);
		}

		// Add it to the new parent

		if (to.parentChildId) {
			const slot = to.slot ?? 'default';
			const parentData = data.children?.[to.parentChildId];

			if (isBlockSerializedChild(parentData)) {
				const slotChildren = parentData.slots[slot]?.slice() ?? [];
				slotChildren.splice(to.index, 0, childId);

				data.children = {
					...data.children,
					[to.parentChildId]: {
						...parentData,
						slots: {
							...parentData.slots,
							[slot]: slotChildren,
						},
					},
				};
			}
			else if (isBlockSerializedSlotOutlet(parentData)) {
				const fallback = parentData.fallback?.slice() ?? [];
				fallback.splice(to.index, 0, childId);

				data.children = {
					...data.children,
					[to.parentChildId]: {
						...parentData,
						fallback,
					},
				};
			}
			else {
				throw new Error(`Invalid to parent child with id ${to.parentChildId}`);
			}
		}
		else {
			const rootChildren = data.rootChildren?.slice() ?? [];
			rootChildren.splice(to.index, 0, childId);
			data.rootChildren = rootChildren;
		}
	}

	return {
		data,
	};
}
